// Ruta: frontend/src/modules/inspections/pages/InspectionFormPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChecklist } from '@core/hooks/useChecklist';
import { useAuth } from '@core/hooks/useAuth';
import { inspectionService } from '@core/services/inspectionService';
import { Button } from '@shared/components/Button';
import { Input } from '@shared/components/Input';
import { Question } from '@core/types';

interface AnswerValue {
  [key: string]: any;
}

const InspectionFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState<any>(null);
  const [answers, setAnswers] = useState<AnswerValue>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [signature, setSignature] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { getChecklistByQR } = useChecklist();
  const { user } = useAuth();

  // Cargar checklist cuando exista id en la URL
  useEffect(() => {
    if (!id) return;
    const loadChecklist = async () => {
      try {
        const data = await getChecklistByQR(id);
        setChecklist(data);
        const initialAnswers: AnswerValue = {};
        data.questions.forEach((q: Question) => {
          initialAnswers[q.id!] = q.type === 'BOOLEAN' ? false : '';
        });
        setAnswers(initialAnswers);
      } catch (err) {
        console.error('Error cargando checklist:', err);
        setError('Checklist no encontrado');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    loadChecklist();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => console.log('Ubicación no disponible')
      );
    }
  }, [id, getChecklistByQR, navigate]);

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!checklist || !user) return;

    const missingRequired = checklist.questions.filter(
      (q: Question) => q.required && (answers[q.id!] === '' || answers[q.id!] === null || answers[q.id!] === undefined)
    );

    if (missingRequired.length > 0) {
      setError('Por favor, responde todas las preguntas obligatorias');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        checklistId: checklist.id,
        location: location || undefined,
        signature: signature || undefined,
        answers: Object.entries(answers).map(([questionId, value]) => ({
          questionId,
          value,
        })),
      };
      await inspectionService.submit(payload);
      alert('Inspección enviada exitosamente');
      navigate('/');
    } catch (err) {
      setError((err as Error).message || 'Error al enviar la inspección');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Cargando checklist...</div>;
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">{error}</div>;
  }

  if (!checklist) {
    return <div className="text-center py-12 text-gray-500">Checklist no encontrado</div>;
  }

  const renderQuestionInput = (question: Question) => {
    const value = answers[question.id!];

    switch (question.type) {
      case 'TEXT':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleAnswerChange(question.id!, e.target.value)}
            placeholder="Escribe tu respuesta..."
            fullWidth
          />
        );
      case 'NUMBER':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleAnswerChange(question.id!, parseFloat(e.target.value) || '')}
            placeholder="0"
            fullWidth
          />
        );
      case 'BOOLEAN':
        return (
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                checked={value === true}
                onChange={() => handleAnswerChange(question.id!, true)}
                className="rounded border-gray-300 text-blue-600"
              />
              <span>Sí</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                checked={value === false}
                onChange={() => handleAnswerChange(question.id!, false)}
                className="rounded border-gray-300 text-blue-600"
              />
              <span>No</span>
            </label>
          </div>
        );
      case 'CHOICE':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleAnswerChange(question.id!, e.target.value)}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">Selecciona...</option>
            {question.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      case 'DATE':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => handleAnswerChange(question.id!, e.target.value)}
            fullWidth
          />
        );
      case 'PHOTO':
        return (
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      handleAnswerChange(question.id!, ev.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                };
                input.click();
              }}
            >
              Subir foto
            </Button>
            {value && (
              <div className="mt-2">
                <img src={value} alt="Foto" className="max-w-xs max-h-48 rounded-lg" />
              </div>
            )}
          </div>
        );
      default:
        return <p className="text-gray-500">Tipo de pregunta no soportado</p>;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{checklist.title}</h1>
        <Button type="submit" isLoading={isSubmitting}>
          Enviar inspección
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {checklist.description && (
        <p className="text-gray-600">{checklist.description}</p>
      )}

      <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
        {checklist.questions.map((question: Question, index: number) => (
          <div key={question.id} className="p-6">
            <div className="flex items-start space-x-2">
              <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {question.text}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </p>
                <div className="mt-2">{renderQuestionInput(question)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Firma</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
          <Input
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="URL de la firma o firma digital"
            fullWidth
          />
          <Button
            variant="secondary"
            className="mt-2"
            type="button"
            onClick={() => {
              const canvas = document.createElement('canvas');
              canvas.width = 400;
              canvas.height = 200;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.fillStyle = '#f3f4f6';
                ctx.fillRect(0, 0, 400, 200);
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(50, 100);
                ctx.lineTo(150, 50);
                ctx.lineTo(250, 150);
                ctx.lineTo(350, 80);
                ctx.stroke();
                setSignature(canvas.toDataURL('image/png'));
              }
            }}
          >
            Generar firma de ejemplo
          </Button>
        </div>
      </div>
    </form>
  );
};

export default InspectionFormPage;